import { skipToken } from "@reduxjs/toolkit/query";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { convertCoachingContentToStructuredContent } from "@repo/app-types/entities/teammatePlaybookCoaching";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { GradingReferenceProvider } from "@repo/ui/structuredContent/gradingReferenceContext";
import { renderStructuredContent } from "@repo/ui/structuredContent/structuredContentRenderer";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import type { FC } from "react";
import { Heading } from "react-aria-components";
import { usePlaybookTeammateCoachingQuery } from "../../api/entities/performancePlaybook.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { findCurrentCriterionCoachingNote } from "./criterionDialogHelpers.js";
import { GradingReferenceLinkAdapter } from "./gradingReferenceLinkAdapter.js";

interface CriterionTakeawayProps {
  playbookId: PlaybookId;
  teammateId: TeammateId;
  criterionId: string;
}

export const CriterionTakeaway: FC<CriterionTakeawayProps> = (props) => {
  const { playbookId, teammateId, criterionId } = props;
  const now = useNow();
  const isEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.TeammatePlaybookOpportunities,
  );
  const {
    currentData: coaching,
    isFetching,
    isError,
  } = usePlaybookTeammateCoachingQuery(
    isEnabled ? { playbookId, teammateId } : skipToken,
  );
  const note = findCurrentCriterionCoachingNote(
    isEnabled && !isError ? coaching : undefined,
    criterionId,
    now,
  );
  const isLoading = isEnabled && isFetching && !coaching;
  if (!note && !isLoading) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3">
      <Heading level={2} className="text-base font-semibold text-foreground">
        Takeaway
      </Heading>
      {note ? (
        <GradingReferenceProvider value={GradingReferenceLinkAdapter}>
          <div className="text-sm text-foreground">
            {renderStructuredContent(
              convertCoachingContentToStructuredContent(note),
              { playbookId, teammateId, criterionId },
            )}
          </div>
        </GradingReferenceProvider>
      ) : (
        <div className="animate-pulse space-y-2" aria-label="Loading takeaway">
          <div className="h-4 w-full rounded bg-gray-200" />
          <div className="h-4 w-2/3 rounded bg-gray-200" />
        </div>
      )}
    </section>
  );
};
